/* eslint-disable react-hooks/exhaustive-deps */
import { Avatar, Box, Chip, IconButton, InputBase, Skeleton, Step, StepConnector, StepLabel, Stepper, Switch, Tooltip, Typography, useTheme } from "@mui/material";
import React from "react";
import { Icon } from "@iconify/react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import { useNavigate, useParams } from "react-router-dom";
import { alpha, styled, keyframes } from '@mui/material/styles';
import { saveWhatsappChatMetaData, searchContactFromLeadRepo, fetchAIWizardStatus, fetchAIConfig, syncTemplates, updateSearchMode, isWhatsappSelectionForPractice } from "src/redux/slices/whatsappV2";
import useTemplateAutomation, { triggerLabel } from 'src/hooks/useTemplateAutomation';
import useIsSuperAdmin from 'src/hooks/useIsSuperAdmin';
import { useAuth } from 'src/hooks';
import { formatTemplateName } from 'src/components/whatsappComponents/whatsAppTemplateUtils';
import { filterNormalWhatsAppTemplates } from 'src/components/whatsappComponents/quickReplyUtils';
import TemplatePreviewDrawer from 'src/components/templates/TemplatePreviewDrawer';
import { callApi } from 'src/utils/apiCall';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { piiProps } from 'src/components/DemoBlur';
import AISetupWizard from './AISetupWizard/AISetupWizard';
import WhatsappTemplateModal from './WhatsappTemplatePreview';
import { getStyles, getAvatarColor } from "./styles";
import { femaleAvatarList, hashString, maleAvatarList, stringAvatar, parseWhatsappTimestamp, readThreadId } from "./whatsappUtil";

// Match the wizard's stepper visual exactly so the configured-state
// summary feels like the wizard collapsed into a single read-only row.
const FeatureConnector = styled(StepConnector)({
    '& .MuiStepConnector-line': {
        borderColor: '#EBEBEB',
        borderTopWidth: 2,
    },
    '&.Mui-active .MuiStepConnector-line, &.Mui-completed .MuiStepConnector-line': {
        borderColor: '#00A76F',
    },
});

function FeatureStepIcon({ completed }) {
    return (
        <Box sx={{
            width: 24, height: 24, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, fontWeight: 700,
            backgroundColor: completed ? '#00A76F' : '#EBEBEB',
            color: completed ? '#fff' : '#B0B0B0',
        }}>
            {completed ? '✓' : '✕'}
        </Box>
    );
}

// Gentle pulse for the status dot
const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// Rotation used by the sync spinner icon
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const AIDot = styled(Box, { shouldForwardProp: (prop) => prop !== 'active' })(({ active }) => ({
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: active ? '#22C55E' : '#919EAB',
    animation: active ? `${pulse} 2s ease-in-out infinite` : 'none',
    flexShrink: 0,
}));

// #18 — per-user, per-practice localStorage key for the AI Setup card
// dismissal. Mirrors the NHSInfoBanner localStorage-dismissal pattern but
// keyed by user so one user's dismissal never hides the card for teammates
// on a shared machine.
const aiSetupDismissKey = (userId, practiceId) =>
    `whatsapp_ai_setup_dismissed_${userId ?? 'anon'}_${practiceId ?? 'na'}`;

const SearchLeadForWhatsapp = () => {
    const theme = useTheme();
    const styles = getStyles(theme);
    const dispatch = useDispatch();
    const { practiceId } = useParams();
    const { user } = useAuth();

    // Automation toggle — workflows that send WA templates for this practice
    const { hintMap, inflight: automationInflight, setEnabled: setWorkflowEnabled } = useTemplateAutomation(practiceId);
    const { isSuperAdminUser } = useIsSuperAdmin();
    const canToggleAutomation = isSuperAdminUser();

    const {
        leadSearchResult,
        whatsappSearchLeadLoading,
        isLoading,
        aiWizardStatus,
        aiWizardConfig,
        filterCounts,
        whatsppTemplates,
        currentBusinessId,
        currentWhatsappPhoneId,
        currentBusinessIsConnected,
        selectionIsForPractice,
    } =
        useSelector(
            (state) => ({
                leadSearchResult: state?.whatsappV2?.leadSearchResult,
                whatsappSearchLeadLoading: state?.whatsappV2?.whatsappSearchLeadLoading,
                isLoading: state?.whatsappV2?.isLoading,
                aiWizardStatus: state?.whatsappV2?.aiWizardStatus,
                aiWizardConfig: state?.whatsappV2?.aiWizardConfig,
                filterCounts: state?.whatsappV2?.filterCounts,
                whatsppTemplates: state?.whatsappV2?.whatsppTemplates,
                currentBusinessId: state?.whatsappV2?.currentBusinessId,
                currentWhatsappPhoneId: state?.whatsappV2?.currentWhatsappPhoneId,
                currentBusinessIsConnected: state?.whatsappV2?.currentBusinessIsConnected,
                // practiceId here comes from the route, which can move before
                // whatsappV2 re-initialises (JAVASCRIPT-REACT-1HW).
                selectionIsForPractice: isWhatsappSelectionForPractice(state?.whatsappV2, practiceId),
            }),
            shallowEqual
        );

    const [wizardOpen, setWizardOpen] = React.useState(false);
    // Tracks an explicit user-close so that a background refetch doesn't
    // yank the wizard back open. Bug 1 of the AI flicker RCA.
    // #18 — now seeded from localStorage (per user + practice) so the
    // dismissal survives reloads instead of the card reappearing every
    // time the page mounts. Manual open via the "Set up" button still works.
    const userClosedWizardRef = React.useRef(false);
    React.useEffect(() => {
        try {
            userClosedWizardRef.current =
                localStorage.getItem(aiSetupDismissKey(user?.pk, practiceId)) === 'true';
        } catch (_) {
            userClosedWizardRef.current = false;
        }
    }, [user?.pk, practiceId]);
    // Tracks whether we've ever received a successful AI status response,
    // so we can show a skeleton on FIRST load instead of the "no AI" empty
    // state. Bug 2 of the AI flicker RCA.
    const [hasLoadedAiStatus, setHasLoadedAiStatus] = React.useState(false);

    // Fetch wizard status on mount only. We deliberately do NOT refetch on
    // every window focus — that was Bug 3 (caused thrash + reopen flicker).
    React.useEffect(() => {
        if (practiceId) {
            dispatch(fetchAIWizardStatus(practiceId));
        }
    }, [practiceId]);

    // Mark "loaded" the moment status is non-null (success or empty).
    React.useEffect(() => {
        if (aiWizardStatus !== null && aiWizardStatus !== undefined) {
            setHasLoadedAiStatus(true);
        }
    }, [aiWizardStatus]);

    // Auto-open wizard when feature enabled but not yet configured; close if disabled.
    // Respect userClosedWizardRef so a refetch doesn't reopen against the user's wish.
    React.useEffect(() => {
        if (aiWizardStatus?.wizard_enabled && !aiWizardStatus?.wizard_completed) {
            dispatch(fetchAIConfig(practiceId));
            if (!userClosedWizardRef.current) setWizardOpen(true);
        }
        if (aiWizardStatus?.wizard_enabled && aiWizardStatus?.wizard_completed) {
            dispatch(fetchAIConfig(practiceId));
        }
        if (aiWizardStatus && !aiWizardStatus?.wizard_enabled) {
            setWizardOpen(false);
        }
    }, [aiWizardStatus, aiWizardStatus?.wizard_completed]);

    const [searchKey, setSearchKey] = React.useState('');

    // #20 — recent missed calls list (server-side `missed=true` filter).
    // Mirrors the LaunchpadMessages missed-call fetch + name resolution
    // (LaunchpadMessages.jsx ~258-296) but uses the BE filter instead of
    // client-side direction/answered_at filtering. Rows: {id, name,
    // threadId, time}. Click opens the thread.
    const navigate = useNavigate();
    const [missedCalls, setMissedCalls] = React.useState([]);
    React.useEffect(() => {
        let cancelled = false;
        // The account/phone must belong to the practice in the URL; otherwise
        // this is the previous practice's selection and the request 404s.
        if (!selectionIsForPractice) return undefined;
        (async () => {
            try {
                const phoneBase = `/api/v1/practices/${practiceId}/whatsapp-business-accounts/${currentBusinessId}/whatsapp-phones/${currentWhatsappPhoneId}`;
                const res = await callApi(`${phoneBase}/calls/?missed=true&limit=5&ordering=-created_at`, 'GET');
                if (!res?.ok || cancelled) {
                    if (!res?.ok) console.error('Error fetching missed calls', res?.status);
                    return;
                }
                const calls = Array.isArray(res?.data?.results) ? res.data.results : [];
                // Resolve caller names in one batch — the call row carries lead
                // as a bare id (same approach as LaunchpadMessages).
                const leadIds = [...new Set(calls.map((c) => c.lead).filter(Boolean))];
                const nameById = {};
                if (leadIds.length > 0) {
                    const leadsRes = await callApi(
                        `/api/v1/practices/${practiceId}/leads/?id__in=${leadIds.join(',')}&fields={id,first_name,last_name}&limit=${leadIds.length}`, 'GET'
                    );
                    (leadsRes?.data?.results || []).forEach((l) => {
                        nameById[l.id] = [l.first_name, l.last_name].filter(Boolean).join(' ');
                    });
                }
                if (!cancelled) {
                    setMissedCalls(calls.map((c) => ({
                        id: c.id,
                        name: (c.lead && nameById[c.lead]) || c.peer_waid || '—',
                        threadId: readThreadId(c.thread),
                        time: c.initiated_at || c.created_at,
                    })));
                }
            } catch (err) {
                console.error('Error fetching missed calls', err);
            }
        })();
        return () => { cancelled = true; };
    }, [practiceId, currentBusinessId, currentWhatsappPhoneId, selectionIsForPractice]);

    const handleMissedCallClick = (item) => {
        if (item?.threadId) {
            navigate(PATH_DASHBOARD.whatsapp.thread(practiceId, item.threadId));
        }
    };

    // Time-ago label for a missed-call row ("2h ago"); falls back to the
    // raw string when the wire format is unparseable.
    const missedCallTimeAgo = (raw) => {
        const m = parseWhatsappTimestamp(raw);
        return m ? m.fromNow() : (raw || '');
    };

    const handleContactSearch = React.useCallback(
        debounce((val) => {
            dispatch(searchContactFromLeadRepo(val));
        }, 300),
        []
    );

    const handleNewContactClick = (contact, fullName, phone, avatarUrl) => {
        dispatch(saveWhatsappChatMetaData({ leadId: contact?.id, name: fullName, phoneNumber: phone, avatar: avatarUrl }));
    };

    const wizardEnabled = aiWizardStatus?.wizard_enabled;
    const wizardCompleted = aiWizardStatus?.wizard_completed;
    const isAiActive = wizardCompleted && aiWizardStatus?.ai_master_toggle !== false;

    // Only show AI section when feature enabled and not actively searching
    const showAISection = wizardEnabled && searchKey.length === 0 && !whatsappSearchLeadLoading;
    // True while we're still waiting for the very first wizard-status response.
    // Used to render a skeleton instead of the "no AI" fallback so the panel
    // doesn't flash "AI not configured" at every mount.
    const aiStatusLoading = !hasLoadedAiStatus && searchKey.length === 0 && !whatsappSearchLeadLoading;

    // Templates derived from the whatsappV2 slice (already loaded by ChatPage
    // initialisation when a business id is known). Empty until then — we
    // gracefully degrade to a "—" count rather than firing a new fetch.
    const rawTemplateList = Array.isArray(whatsppTemplates?.[currentBusinessId])
        ? whatsppTemplates[currentBusinessId]
        : [];
    const templateList = React.useMemo(
        () => filterNormalWhatsAppTemplates(rawTemplateList),
        [rawTemplateList]
    );
    const templateCount = templateList.length;

    // Status-bucket the templates so the Templates section can surface
    // "needs attention" prominently and show approved/pending/rejected
    // counts at a glance.
    const norm = (t) => String(t?.status || '').toUpperCase();
    const templateApproved = templateList.filter((t) => norm(t) === 'APPROVED');
    const templatePending = templateList.filter((t) => norm(t) === 'PENDING' || norm(t) === 'IN_APPEAL' || norm(t) === 'PENDING_DELETION');
    const templateRejected = templateList.filter((t) => norm(t) === 'REJECTED' || norm(t) === 'DISABLED' || norm(t) === 'PAUSED');
    // Most-used proxy: prefer Meta-provided usage if available; otherwise
    // fall back to recently-created order (templateList is typically
    // returned newest-first by the BE).
    const quickSendTemplates = (templateList.length > 0
        ? [...templateList].sort((a, b) => (b?.use_count ?? 0) - (a?.use_count ?? 0))
        : []
    ).filter((t) => norm(t) !== 'REJECTED' && norm(t) !== 'DISABLED').slice(0, 3);

    // Category → emoji + colour for the quick-send tile glyph. Falls back
    // to a generic document icon if the category is unknown.
    const categoryGlyph = (cat) => {
        const k = String(cat || '').toUpperCase();
        if (k.includes('UTILITY')) return { icon: 'mdi:calendar-check-outline', color: '#3b82f6' };
        if (k.includes('MARKETING')) return { icon: 'mdi:megaphone-outline', color: '#f59e0b' };
        if (k.includes('AUTHENTICATION')) return { icon: 'mdi:shield-key-outline', color: '#8b5cf6' };
        return { icon: 'mdi:file-document-outline', color: '#64748b' };
    };
    const statusDot = (status) => {
        const k = String(status || '').toUpperCase();
        if (k === 'APPROVED') return '#22c55e';
        if (k === 'PENDING' || k === 'IN_APPEAL') return '#f59e0b';
        if (k === 'REJECTED' || k === 'DISABLED' || k === 'PAUSED') return '#ef4444';
        return '#94a3b8';
    };

    // Local template-modal state — re-uses the same component the chat header
    // and composer toolbar use, so create/edit/send paths are identical.
    const [templateModalOpen, setTemplateModalOpen] = React.useState(false);
    const [templateModalIndex, setTemplateModalIndex] = React.useState(0);
    // Status pre-filter for the Browse/count-click paths. Default 'ALL' so
    // the Browse button behaves unchanged; count clicks pass a specific status.
    const [templateModalStatus, setTemplateModalStatus] = React.useState('ALL');
    // Template to preview in the TemplatePreviewDrawer (automation eye-icon path).
    // One drawer instance shared across all automation rows.
    const [previewTemplate, setPreviewTemplate] = React.useState(null);

    // Sync — mirrors the Selector's own handleSync; dispatches the existing
    // syncTemplates() thunk which updates whatsppTemplates in Redux (and
    // therefore templateList here) automatically.
    const [syncing, setSyncing] = React.useState(false);
    const canWriteProvider = currentBusinessIsConnected === true;
    const providerAccount = React.useMemo(() => ({
        id: currentBusinessId,
        is_connected: currentBusinessIsConnected,
    }), [currentBusinessId, currentBusinessIsConnected]);
    let syncButtonTitle = 'Sync approval status from Meta';
    let syncButtonCursor = 'pointer';
    let syncButtonOpacity = 1;
    if (!canWriteProvider) {
        syncButtonTitle = 'Switch to a connected WhatsApp account to sync templates';
        syncButtonCursor = 'not-allowed';
        syncButtonOpacity = 0.5;
    } else if (syncing) {
        syncButtonTitle = 'Checking approval status…';
        syncButtonCursor = 'wait';
        syncButtonOpacity = 0.7;
    }
    const handleHubSync = React.useCallback(async () => {
        if (!canWriteProvider || syncing) return;
        setSyncing(true);
        try { await dispatch(syncTemplates()); } finally { setSyncing(false); }
    }, [canWriteProvider, syncing, dispatch]);

    // Open the template modal pre-filtered to a status (or 'ALL' for Browse).
    const openTemplateSelector = React.useCallback((status) => {
        setTemplateModalStatus(status);
        setTemplateModalIndex(0);
        setTemplateModalOpen(true);
    }, []);

    // Solid KPI card — iOS-style: solid white fill, soft radius, hairline
    // border. Responsive: tight on narrow panels, generous padding on
    // wider screens. No translucency — widgets must be easy to read.
    // F7 — `onClick` makes the card an actionable shortcut (switches the
    // sidebar to the matching filter tab). Cards without onClick stay
    // purely informational.
    const StatCard = ({ label, value, icon, color, info, onClick, subLabel }) => (
        <Box
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            aria-label={onClick ? `Filter conversations: ${label}` : undefined}
            onClick={onClick}
            onKeyDown={onClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            } : undefined}
            sx={{
            flex: 1,
            minWidth: 0,
            borderRadius: { xs: '14px', md: '16px' },
            backgroundColor: '#ffffff',
            border: '1px solid rgba(145, 158, 171, 0.16)',
            px: { xs: '8px', md: '12px' },
            py: { xs: '7px', md: '10px' },
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '2px', md: '4px' },
            position: 'relative',
            overflow: 'hidden',
            cursor: onClick ? 'pointer' : 'default',
            transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 220ms',
            '&:hover': {
                transform: 'translateY(-1px)',
                borderColor: 'rgba(145, 158, 171, 0.32)',
            },
        }}>

            <Tooltip title={info || ''} arrow placement="top" disableHoverListener={!info}
                slotProps={{ tooltip: { sx: { bgcolor: '#1e293b', fontSize: '12px', maxWidth: 280 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '5px', md: '6px' }, cursor: info ? 'help' : 'default' }}>
                    <Box sx={{
                        width: { xs: 16, md: 20 },
                        height: { xs: 16, md: 20 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: `${color}15`,
                        color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <Icon icon={icon} width="60%" height="60%" />
                    </Box>
                    <Typography sx={{
                        fontSize: { xs: '0.6875rem', md: '0.75rem' },
                        fontWeight: 600,
                        color: '#637381',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {label}
                    </Typography>
                    {info && (
                        <Icon icon="mdi:information-outline" width={11} height={11} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                    )}
                </Box>
            </Tooltip>
            <Typography sx={{
                fontSize: { xs: '0.9375rem', md: '1.125rem', lg: '1.25rem' },
                fontWeight: 700,
                lineHeight: 1.1,
                color: '#212B36',
                // Dim 0 / "—" so real values stand out at a glance.
                opacity: (value === 0 || value === '—') ? 0.45 : 1,
            }}>
                {value ?? '—'}
            </Typography>
            {/* #19 — optional age-breakdown sub-label (e.g. "3 waiting >7d").
                Only rendered when provided, so all other cards are unchanged. */}
            {subLabel ? (
                <Typography sx={{
                    fontSize: { xs: '0.5625rem', md: '0.625rem' },
                    fontWeight: 600,
                    color: '#f59e0b',
                    lineHeight: 1.2,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {subLabel}
                </Typography>
            ) : null}
        </Box>
    );

    // F7 — clicking an activity widget switches the sidebar to the matching
    // filter tab (mirrors Contacts.jsx handleSearchMode; `user` is only
    // meaningful for the MY mode, so null here). Widgets without a matching
    // sidebar tab (Window, New leads) stay informational — deferred per plan.
    const handleWidgetModeClick = (mode) => {
        dispatch(updateSearchMode({ mode, user: null }));
    };

    // Solid section surface — iOS-style soft fill, generous radius, hairline
    // border. No translucency or backdrop-blur (those made the widgets hard
    // to read). Clean white card on the page tint.
    const glassSx = {
        backgroundColor: '#ffffff',
        borderRadius: '20px',
        border: '1px solid rgba(145, 158, 171, 0.16)',
        overflow: 'hidden',
    };

    return (
        <Box sx={{
            ...styles.containerStyle,
            // Soft off-white page tint so cards have a subtle contrast edge
            // without going to a hard grey.
            backgroundColor: '#fafbfc',
        }}>
            <Box
                sx={{
                    // Responsive padding & gap — tighter on narrow panels,
                    // more breathing room on bigger screens.
                    p: { xs: '1rem', md: '1.25rem', lg: '1.5rem 1.75rem' },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: '0.875rem', md: '1.125rem', lg: '1.5rem' },
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    // Soft max-width so the Hub doesn't sprawl on very wide
                    // screens — keeps the row proportions readable.
                    width: '100%',
                    maxWidth: { xs: '100%', xl: '900px' },
                    mx: 'auto',
                }}
            >
                {/* Compact section label — same uppercase 10px style the
                    ResolvePanel uses for its section labels (SUB_LABEL). */}
                <Box>
                    <Typography sx={{
                        fontSize: '10px',
                        color: '#919EAB',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        fontWeight: 600,
                        mb: '6px',
                    }}>
                        Start new conversation
                    </Typography>
                    {/* Search Input — Resolve-panel TextField style: 10px radius,
                        12px font, hairline border, accent on focus. */}
                    <Box sx={{ position: 'relative' }}>
                        <Icon
                            icon="mdi:magnify"
                            style={{
                                position: 'absolute',
                                left: '0.625rem',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#919EAB',
                                width: '0.875rem',
                                height: '0.875rem',
                            }}
                        />
                        <InputBase
                            placeholder="Search by name, phone, or email…"
                            disabled={isLoading}
                            onChange={(e) => {
                                setSearchKey(e.target.value);
                                handleContactSearch(e.target.value);
                            }}
                            sx={{
                                width: '100%',
                                padding: '6px 10px 6px 28px',
                                borderRadius: '10px',
                                backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                fontSize: '12px',
                                border: '1px solid rgba(145, 158, 171, 0.16)',
                                transition: 'all 0.15s',
                                '&:focus-within': {
                                    borderColor: '#00AB55',
                                    backgroundColor: '#fff',
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Always-visible hint right under the search bar — replaces the
                    duplicated copies that previously lived under the AI summary
                    card and under the "not configured" fallback. */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && (
                    <Typography sx={{
                        fontSize: '0.75rem',
                        color: '#919EAB',
                        textAlign: 'center',
                        mt: '-0.5rem',
                    }}>
                        Select a conversation on the left to start messaging
                    </Typography>
                )}

                {/* Activity widgets — only when not actively searching */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Activity
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        {/* #22 — user design decision: exactly THREE cards, in this
                            order: Needs reply · Missed calls · Waiting. The former
                            Window / New leads / Closed / Archived cards were removed;
                            Closed + Archived counts still feed the sidebar "More"
                            menu badges (Contacts.jsx), so their fetches remain. */}
                        <Box sx={{ display: 'flex', gap: '6px' }}>
                            {/* F1: direction-based semantics — "Needs reply" mirrors the
                                Needs-reply tab (needs_reply=true) and "Waiting" mirrors
                                the Waiting-on-patient tab (needs_reply=false), so
                                widget == tab == badge == list. */}
                            <StatCard
                                label="Needs reply"
                                value={filterCounts?.FOLLOW_UP_COUNT ?? 0}
                                icon="mdi:email-arrow-right-outline"
                                color="#3b82f6"
                                info="Conversations that need a reply — the patient sent the last message or left a missed call. Reading doesn't clear this; reply or resolve does. Matches the sidebar's Needs reply tab."
                                onClick={() => handleWidgetModeClick('FOLLOW_UP')}
                            />
                            {/* F8 — missed-call count from calls/counts/
                                (server-computed `missed`). Informational for
                                now: no sidebar tab filters threads by missed
                                calls; missed-call threads surface in Needs
                                reply (needs_reply covers unanswered calls). */}
                            <StatCard
                                label="Missed calls"
                                value={filterCounts?.MISSED_CALLS_COUNT ?? 0}
                                icon="mdi:phone-missed"
                                color="#ef4444"
                                info="Inbound WhatsApp calls that went unanswered. These conversations also appear in Needs reply until you call back, reply or resolve."
                            />
                            <StatCard
                                label="Waiting"
                                value={filterCounts?.PENDING_COUNT ?? 0}
                                icon="mdi:clock-outline"
                                color="#f59e0b"
                                info="Conversations where you sent the last message and are waiting on the patient to respond. Matches the sidebar's Waiting on patient tab."
                                onClick={() => handleWidgetModeClick('PENDING')}
                                subLabel={(filterCounts?.WAITING_OVER_7D_COUNT ?? 0) > 0
                                    ? `${filterCounts.WAITING_OVER_7D_COUNT} waiting >7d`
                                    : null}
                            />
                        </Box>
                    </Box>
                )}

                {/* #20 — Missed calls list: recent 5 unanswered inbound calls
                    (server-side missed=true). Same divider-header structure as
                    Activity / WhatsApp AI / Templates. Hidden when empty. */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && missedCalls.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Missed calls
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <Box sx={glassSx}>
                            {missedCalls.map((call, idx) => (
                                <Box
                                    key={call.id}
                                    // Review nit (#20): rows without a threadId aren't
                                    // actionable — don't expose a dead button to screen
                                    // readers or the tab order.
                                    role={call.threadId ? 'button' : undefined}
                                    tabIndex={call.threadId ? 0 : undefined}
                                    aria-label={call.threadId ? `Open conversation with ${call.name}` : undefined}
                                    onClick={() => handleMissedCallClick(call)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleMissedCallClick(call);
                                        }
                                    }}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        px: '14px', py: '10px',
                                        cursor: call.threadId ? 'pointer' : 'default',
                                        borderBottom: idx < missedCalls.length - 1 ? '1px solid rgba(145, 158, 171, 0.08)' : 'none',
                                        '&:hover': { backgroundColor: 'rgba(145, 158, 171, 0.06)' },
                                    }}
                                >
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '8px', flexShrink: 0,
                                        backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon icon="mdi:phone-missed" width={15} height={15} style={{ color: '#ef4444' }} />
                                    </Box>
                                    <Typography {...piiProps} sx={{
                                        flex: 1, minWidth: 0,
                                        fontSize: '0.8125rem', fontWeight: 600, color: '#212B36',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {call.name}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.6875rem', color: '#919EAB', flexShrink: 0 }}>
                                        {missedCallTimeAgo(call.time)}
                                    </Typography>
                                    {call.threadId && (
                                        <Icon icon="mdi:chevron-right" width={16} height={16} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                )}

                {/* Loading State */}
                {whatsappSearchLeadLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {[1, 2, 3].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.75rem',
                                    padding: '0.875rem 1rem',
                                    backgroundColor: 'white',
                                    borderRadius: '0.625rem',
                                    border: '1px solid rgba(145, 158, 171, 0.2)',
                                }}
                            >
                                <Skeleton variant="circular" width={48} height={48} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton width="50%" height={20} sx={{ mb: '0.25rem' }} />
                                    <Skeleton width="30%" height={16} sx={{ mb: '0.25rem' }} />
                                    <Skeleton width="80%" height={14} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Search Results */}
                {!whatsappSearchLeadLoading && leadSearchResult?.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '0.25rem' }}>
                            Search Results ({leadSearchResult.length})
                        </Typography>
                        {leadSearchResult.map((contact) => {
                            const fullName = `${contact?.first_name ?? ''} ${contact?.last_name ?? ''}`.trim();
                            const phone = contact?.lead_contact?.phone ?? '';
                            const avatarIndex = hashString(fullName || phone);
                            const avatarColor = getAvatarColor(fullName);

                            const avatarUrl = (contact?.salutation?.toLowerCase() ?? "mrs")?.includes("mrs")
                                ? femaleAvatarList[avatarIndex % femaleAvatarList.length]
                                : maleAvatarList[avatarIndex % maleAvatarList.length];

                            return (
                                <Box
                                    key={contact?.id}
                                    onClick={() => handleNewContactClick(contact, fullName, phone, avatarUrl)}
                                    sx={{
                                        border: '1px solid rgba(145, 158, 171, 0.16)',
                                        borderRadius: '10px',
                                        padding: '8px 10px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.6)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 167, 111, 0.06)',
                                            borderColor: 'rgba(0, 167, 111, 0.3)',
                                        },
                                    }}
                                >
                                    <Avatar
                                        {...piiProps}
                                        {...stringAvatar(avatarUrl, phone, {
                                            width: 30,
                                            height: 30,
                                            fontSize: '11px',
                                            backgroundColor: avatarColor,
                                            color: 'white',
                                            fontWeight: 600,
                                        })}
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Typography {...piiProps} sx={{ fontWeight: 600, fontSize: '12px', color: '#212B36', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {fullName || 'Unknown'}
                                            </Typography>
                                            {contact?.is_archived && (
                                                <Box sx={{ backgroundColor: 'rgba(255, 86, 48, 0.12)', color: '#B71D18', fontSize: '9px', fontWeight: 700, padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>
                                                    ARCHIVED
                                                </Box>
                                            )}
                                            {contact?.lead_treatments?.[0]?.treatment && (
                                                <Typography sx={{ fontSize: '10px', color: '#B76E00', backgroundColor: 'rgba(255, 171, 0, 0.12)', px: '5px', py: '1px', borderRadius: '4px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '90px' }}>
                                                    {contact?.lead_treatments?.[0]?.treatment}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography {...piiProps} sx={{ fontSize: '11px', color: '#919EAB', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {phone || 'No phone'}
                                            {contact?.assignees?.[0]?.user?.first_name && ` · ${contact.assignees[0].user.first_name}`}
                                        </Typography>
                                    </Box>
                                    <Icon icon="mdi:chevron-right" width={14} height={14} style={{ color: '#C4CDD5', flexShrink: 0 }} />
                                </Box>
                            );
                        })}
                    </Box>
                )}

                {/* Empty Search State */}
                {searchKey.length > 0 && !whatsappSearchLeadLoading && leadSearchResult?.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: '3rem' }}>
                        <Icon icon="mdi:account-search-outline" width={48} height={48} style={{ color: '#919EAB', opacity: 0.5 }} />
                        <Typography sx={{ mt: '0.75rem', fontSize: '0.875rem', color: '#637381' }}>
                            No contacts found for &ldquo;{searchKey}&rdquo;
                        </Typography>
                        <Typography sx={{ mt: '0.25rem', fontSize: '0.8125rem', color: '#919EAB' }}>
                            Try a different name or phone number
                        </Typography>
                    </Box>
                )}


                {/* AI status skeleton — only on the very first load, before
                    we know whether AI is configured. Replaces the brief flash
                    of "no AI" while the wizard-status request is in flight. */}
                {aiStatusLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                WhatsApp AI
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <Box sx={{ ...glassSx, p: '12px' }}>
                            <Skeleton variant="text" width="40%" height={20} />
                            <Skeleton variant="rectangular" height={120} sx={{ mt: '8px', borderRadius: '8px' }} />
                        </Box>
                    </Box>
                )}

                {/* AI Setup Wizard — shown when configuring or editing.
                    Same divider header as the configured/loading states so
                    Activity / WhatsApp AI / Templates all read with parallel
                    section structure. */}
                {showAISection && wizardOpen && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                WhatsApp AI
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <AISetupWizard
                            onClose={() => {
                                userClosedWizardRef.current = true;
                                // #18 — persist the dismissal per user + practice so the
                                // setup card doesn't reappear on every page reload.
                                try {
                                    localStorage.setItem(aiSetupDismissKey(user?.pk, practiceId), 'true');
                                } catch (_) { /* storage unavailable — session-only dismissal */ }
                                setWizardOpen(false);
                                dispatch(fetchAIWizardStatus(practiceId));
                                dispatch(fetchAIConfig(practiceId));
                            }}
                            practiceId={practiceId}
                            initialConfig={aiWizardConfig}
                        />
                    </Box>
                )}

                {/* AI Active/Configured State — summary card with feature status */}
                {showAISection && wizardCompleted && !wizardOpen && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                WhatsApp AI
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>

                        <Box sx={{
                            ...glassSx,
                            // Active AI gets a soft green wash so it reads as alive.
                            backgroundColor: isAiActive ? 'rgba(0, 167, 111, 0.06)' : '#ffffff',
                        }}>
                            {/* Header with status + edit button */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '8px',
                                        background: isAiActive
                                            ? 'linear-gradient(135deg, #00A76F 0%, #007B55 100%)'
                                            : 'rgba(145, 158, 171, 0.16)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon icon="mdi:robot-outline" width={16} height={16} style={{ color: isAiActive ? '#fff' : '#919EAB' }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#212B36' }}>
                                            WhatsApp AI
                                        </Typography>
                                        <AIDot active={isAiActive} />
                                        <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, color: isAiActive ? '#00A76F' : '#919EAB' }}>
                                            {isAiActive ? 'Active' : 'Paused'}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box
                                    component="button"
                                    onClick={() => { dispatch(fetchAIConfig(practiceId)); setWizardOpen(true); }}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: '4px',
                                        border: '1px solid #EBEBEB', borderRadius: '8px',
                                        background: '#fff', px: 1, py: '4px',
                                        cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600,
                                        color: '#637381', fontFamily: 'inherit',
                                        '&:hover': { borderColor: '#00A76F', color: '#00A76F', background: 'rgba(0, 167, 111, 0.04)' },
                                    }}
                                >
                                    <Icon icon="mdi:pencil-outline" width={12} height={12} />
                                    Edit
                                </Box>
                            </Box>

                            {/* Configured-state feature timeline — same MUI Stepper visual
                                as the AISetupWizard's "step" header at the top of this page.
                                Each feature is a step; "completed" = enabled. Connector lines
                                between two enabled features turn green so the at-a-glance
                                shape of what's on shows up as a single coloured run. */}
                            {aiWizardConfig && (() => {
                                const features = [
                                    { id: 'out_of_hours', label: 'Out of Hours', isOn: !!aiWizardConfig.out_of_hours_enabled },
                                    { id: 'treatments', label: 'Treatments', isOn: !!aiWizardConfig.auto_assign_treatment },
                                    { id: 'bookings', label: 'Bookings', isOn: !!aiWizardConfig.appt_ack_enabled },
                                    { id: 'hours', label: 'Hours', isOn: !!aiWizardConfig.auto_reply_hours },
                                    { id: 'address', label: 'Address', isOn: !!aiWizardConfig.auto_reply_location },
                                    { id: 'urgent', label: 'Urgent', isOn: !!aiWizardConfig.urgent_detect },
                                ];
                                return (
                                    <Box sx={{ px: '10px', py: '10px' }}>
                                        <Stepper
                                            alternativeLabel
                                            nonLinear
                                            connector={<FeatureConnector />}
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    fontSize: '0.625rem',
                                                    color: '#919EAB',
                                                    mt: '4px !important',
                                                    '&.Mui-completed': { color: '#00A76F', fontWeight: 600 },
                                                },
                                            }}
                                        >
                                            {features.map((f) => (
                                                <Step key={f.id} completed={f.isOn} active={false}>
                                                    <Tooltip title={`${f.label} — ${f.isOn ? 'On' : 'Off'}`} arrow placement="top">
                                                        <StepLabel StepIconComponent={FeatureStepIcon}>
                                                            {f.label}
                                                        </StepLabel>
                                                    </Tooltip>
                                                </Step>
                                            ))}
                                        </Stepper>
                                    </Box>
                                );
                            })()}
                        </Box>

                    </Box>
                )}

                {/* Templates section — Option A: status-first hub.
                    Surfaces problems (rejected/paused) prominently and gives
                    operators a 1-click "Quick send" path to the most-used
                    templates, while keeping CRUD entry points in the header.
                    All click-targets re-use the same WhatsappTemplateModal
                    the chat composer's "Browse templates" button uses, so
                    create/edit/send flows are identical. */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Templates
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <Box sx={glassSx}>
                            {/* Header — title + CRUD actions */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '8px',
                                        backgroundColor: 'rgba(0, 167, 111, 0.12)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon icon="mdi:file-document-multiple-outline" width={16} height={16} style={{ color: '#007B55' }} />
                                    </Box>
                                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#212B36' }}>
                                        Templates
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Tooltip title={canWriteProvider ? 'Create new template' : 'Switch to a connected WhatsApp account to create templates'} arrow>
                                        <Box component="span" sx={{ display: 'inline-flex' }}>
                                            <Box
                                                component="button"
                                                aria-label="Create new template"
                                                onClick={() => { setTemplateModalIndex(0); setTemplateModalOpen(true); }}
                                                disabled={!canWriteProvider}
                                                sx={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                    border: '1px solid #00A76F', borderRadius: '8px',
                                                    background: '#fff', px: 1, py: '4px',
                                                    cursor: canWriteProvider ? 'pointer' : 'not-allowed', fontSize: '0.6875rem', fontWeight: 600,
                                                    color: '#00A76F', fontFamily: 'inherit',
                                                    opacity: canWriteProvider ? 1 : 0.5,
                                                    '&:hover': { background: 'rgba(0, 167, 111, 0.06)' },
                                                }}
                                            >
                                                <Icon icon="mdi:plus" width={12} height={12} />
                                                New
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                    {/* Sync — checks Meta approval status + refreshes counts */}
                                    <Tooltip title={syncButtonTitle} arrow>
                                        <Box component="span" sx={{ display: 'inline-flex' }}>
                                            <Box
                                                component="button"
                                                aria-label="Sync approval status from Meta"
                                                onClick={handleHubSync}
                                                disabled={!canWriteProvider || syncing}
                                                sx={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    width: 26, height: 26,
                                                    border: '1px solid #EBEBEB', borderRadius: '8px',
                                                    background: '#fff',
                                                    cursor: syncButtonCursor,
                                                    color: '#637381', fontFamily: 'inherit',
                                                    opacity: syncButtonOpacity,
                                                    '&:hover': { borderColor: '#637381', color: '#212B36', background: '#f9fafb' },
                                                }}
                                            >
                                                <Box
                                                    component={Icon}
                                                    icon={syncing ? 'mdi:loading' : 'mdi:sync'}
                                                    width={13} height={13}
                                                    sx={syncing ? { animation: `${spin} 1s linear infinite` } : {}}
                                                />
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                    <Box
                                        component="button"
                                        onClick={() => openTemplateSelector('ALL')}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            border: '1px solid #EBEBEB', borderRadius: '8px',
                                            background: '#fff', px: 1, py: '4px',
                                            cursor: 'pointer', fontSize: '0.6875rem', fontWeight: 600,
                                            color: '#637381', fontFamily: 'inherit',
                                            '&:hover': { borderColor: '#637381', color: '#212B36' },
                                        }}
                                    >
                                        Browse
                                        <Icon icon="mdi:arrow-right" width={12} height={12} />
                                    </Box>
                                </Box>
                            </Box>

                            {/* Status counts row — each count is clickable: opens Browse pre-filtered */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', gap: '12px',
                                px: '14px', py: '8px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.06)',
                                fontSize: '0.6875rem',
                                color: '#637381',
                            }}>
                                <Tooltip title="Browse approved templates" arrow placement="top">
                                    <Box
                                        onClick={() => openTemplateSelector('APPROVED')}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            cursor: 'pointer', borderRadius: '6px', px: '6px', py: '3px',
                                            transition: 'background 150ms',
                                            '&:hover': { background: 'rgba(34, 197, 94, 0.08)' },
                                        }}
                                    >
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e', flexShrink: 0 }} />
                                        <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#212B36' }}>
                                            {templateApproved.length}
                                        </Typography>
                                        <Typography component="span" sx={{ fontSize: '0.6875rem' }}>approved</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Browse pending templates" arrow placement="top">
                                    <Box
                                        onClick={() => openTemplateSelector('PENDING')}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            cursor: 'pointer', borderRadius: '6px', px: '6px', py: '3px',
                                            transition: 'background 150ms',
                                            '&:hover': { background: 'rgba(245, 158, 11, 0.08)' },
                                        }}
                                    >
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#f59e0b', flexShrink: 0 }} />
                                        <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#212B36' }}>
                                            {templatePending.length}
                                        </Typography>
                                        <Typography component="span" sx={{ fontSize: '0.6875rem' }}>pending</Typography>
                                    </Box>
                                </Tooltip>
                                <Tooltip title="Browse templates needing attention" arrow placement="top">
                                    <Box
                                        onClick={() => openTemplateSelector('REJECTED')}
                                        sx={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            cursor: 'pointer', borderRadius: '6px', px: '6px', py: '3px',
                                            transition: 'background 150ms',
                                            '&:hover': { background: 'rgba(239, 68, 68, 0.08)' },
                                        }}
                                    >
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ef4444', flexShrink: 0 }} />
                                        <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: '#212B36' }}>
                                            {templateRejected.length}
                                        </Typography>
                                        <Typography component="span" sx={{ fontSize: '0.6875rem' }}>need attention</Typography>
                                    </Box>
                                </Tooltip>
                            </Box>

                            {/* Needs-attention banner — only when there's something to fix */}
                            {templateRejected.length > 0 && (
                                <Box sx={{
                                    px: '14px', py: '8px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.06)',
                                    borderBottom: '1px solid rgba(239, 68, 68, 0.12)',
                                    display: 'flex', flexDirection: 'column', gap: '4px',
                                }}>
                                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, color: '#b91c1c', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Icon icon="mdi:alert-circle-outline" width={12} height={12} />
                                        Needs attention
                                    </Typography>
                                    {templateRejected.slice(0, 2).map((tpl) => {
                                        const idx = templateList.findIndex((t) => t === tpl);
                                        return (
                                            <Box key={tpl?.id ?? tpl?.name}
                                                onClick={() => { setTemplateModalIndex(idx); setTemplateModalOpen(true); }}
                                                sx={{
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                    cursor: 'pointer', py: '2px',
                                                    '&:hover': { '& .tpl-name': { textDecoration: 'underline' } },
                                                }}>
                                                <Typography className="tpl-name" sx={{ fontSize: '0.75rem', color: '#3D3D3D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                                    {tpl?.name ?? 'Untitled'}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.625rem', color: '#b91c1c', fontWeight: 600, ml: 1 }}>
                                                    {String(tpl?.status || 'rejected').toLowerCase()}
                                                </Typography>
                                                <Icon icon="mdi:chevron-right" width={14} height={14} style={{ color: '#b91c1c', marginLeft: 4 }} />
                                            </Box>
                                        );
                                    })}
                                    {templateRejected.length > 2 && (
                                        <Typography sx={{ fontSize: '0.625rem', color: '#b91c1c' }}>
                                            +{templateRejected.length - 2} more — click Browse to view all
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Frequent templates — click to preview/edit in the
                                existing modal. Sending requires picking a thread
                                first; we don't try to do recipient-picking here. */}
                            {quickSendTemplates.length > 0 ? (
                                <Box sx={{ p: '10px' }}>
                                    <Typography sx={{ fontSize: '0.625rem', fontWeight: 700, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.4px', mb: '6px' }}>
                                        Frequent
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: '6px' }}>
                                        {quickSendTemplates.map((tpl) => {
                                            const idx = templateList.findIndex((t) => t === tpl);
                                            const g = categoryGlyph(tpl?.category);
                                            return (
                                                <Tooltip key={tpl?.id ?? tpl?.name} title={`${tpl?.name ?? 'Untitled'} — ${String(tpl?.status || '').toLowerCase()}`} arrow>
                                                    <Box
                                                        onClick={() => { setTemplateModalIndex(idx); setTemplateModalOpen(true); }}
                                                        sx={{
                                                            flex: 1, minWidth: 0,
                                                            cursor: 'pointer',
                                                            borderRadius: '12px',
                                                            border: '1px solid rgba(145, 158, 171, 0.16)',
                                                            backgroundColor: '#ffffff',
                                                            p: '8px',
                                                            display: 'flex', flexDirection: 'column', gap: '4px',
                                                            transition: 'transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 220ms, background-color 220ms',
                                                            '&:hover': {
                                                                transform: 'translateY(-1px) scale(1.01)',
                                                                borderColor: '#00A76F',
                                                                backgroundColor: 'rgba(0, 167, 111, 0.04)',
                                                            },
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                            <Box sx={{
                                                                width: 22, height: 22, borderRadius: '6px',
                                                                backgroundColor: `${g.color}15`,
                                                                color: g.color,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}>
                                                                <Icon icon={g.icon} width={13} height={13} />
                                                            </Box>
                                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: statusDot(tpl?.status) }} />
                                                        </Box>
                                                        <Typography sx={{
                                                            fontSize: '0.6875rem', fontWeight: 600, color: '#212B36',
                                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                                        }}>
                                                            {tpl?.name ?? 'Untitled'}
                                                        </Typography>
                                                        <Typography sx={{ fontSize: '0.5625rem', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                                            {String(tpl?.category || '—').toLowerCase()}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center', py: '1rem', px: '14px' }}>
                                    <Typography sx={{ fontSize: '0.75rem', color: '#919EAB' }}>
                                        {templateCount === 0
                                            ? 'No templates yet — click New to create one or Browse to sync from Meta.'
                                            : 'Open a conversation or click Browse to load templates.'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Automations section — tasks-v2 row-list pattern.
                    Visible when not searching and at least one automation exists.
                    3px coloured left border: ON=green, OFF=transparent.
                    Admin/superadmin get a live Switch; others get a read-only chip.
                    Eye icon opens TemplatePreviewDrawer for the linked template. */}
                {searchKey.length === 0 && !whatsappSearchLeadLoading && Object.keys(hintMap).length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {/* Section divider — consistent with Activity/Templates/AI headers */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                            <Typography sx={{ px: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                Automations
                            </Typography>
                            <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(145, 158, 171, 0.16)' }} />
                        </Box>
                        <Box sx={glassSx}>
                            {/* Card header — filterSectionStyle title + active count chip */}
                            <Box sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px',
                                borderBottom: '1px solid rgba(145, 158, 171, 0.08)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Box sx={{
                                        width: 28, height: 28, borderRadius: '8px',
                                        backgroundColor: alpha(theme.palette.success.main, 0.12),
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <Icon icon="mdi:robot-outline" width={16} height={16} style={{ color: theme.palette.success.dark }} />
                                    </Box>
                                    {/* tasks-v2 filterSectionStyle: 0.68rem, 700, uppercase, 0.06em spacing */}
                                    <Typography sx={{
                                        fontSize: '0.68rem', fontWeight: 700,
                                        textTransform: 'uppercase', letterSpacing: '0.06em',
                                        color: 'text.disabled',
                                    }}>
                                        WhatsApp Automations
                                    </Typography>
                                </Box>
                                {/* Active count chip — tasks-v2 success.lighter style */}
                                {Object.values(hintMap).flat().filter((h) => h.is_enabled).length > 0 && (
                                    <Chip
                                        size="small"
                                        label={`${Object.values(hintMap).flat().filter((h) => h.is_enabled).length} active`}
                                        sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            bgcolor: alpha(theme.palette.success.main, 0.12),
                                            color: theme.palette.success.darker,
                                            borderRadius: '8px',
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                )}
                            </Box>

                            {/* One row per template-name → workflow mapping.
                                tasks-v2 ListItem pattern: 3px left border (green ON / transparent OFF),
                                soft alpha fill, name + subtitle, right-side controls. */}
                            {Object.entries(hintMap).map(([templateName, hints]) =>
                                hints.map((hint) => {
                                    const isInflight = automationInflight?.has(hint.workflow_id);
                                    // Resolve the full template object for the preview drawer.
                                    // Automation mappings may legitimately reference a reserved
                                    // template, so name resolution deliberately uses the raw list.
                                    const tplObj = rawTemplateList.find((t) => t?.name === templateName) || null;
                                    return (
                                        <Box
                                            key={hint.workflow_id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                py: 1.25,
                                                px: 2,
                                                borderBottom: '1px solid rgba(145, 158, 171, 0.06)',
                                                // tasks-v2 left-border state indicator
                                                borderLeft: hint.is_enabled
                                                    ? `3px solid ${theme.palette.success.main}`
                                                    : '3px solid transparent',
                                                backgroundColor: hint.is_enabled
                                                    ? alpha(theme.palette.success.main, 0.04)
                                                    : theme.palette.background.paper,
                                                '&:last-child': { borderBottom: 'none' },
                                                transition: 'background-color 150ms',
                                            }}
                                        >
                                            {/* Template name + trigger subtitle */}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{
                                                    fontWeight: 600,
                                                    fontSize: '0.82rem',
                                                    color: 'text.primary',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {formatTemplateName(templateName)}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                                    Auto-sends when{' '}
                                                    <Box component="span" sx={{ fontWeight: 600 }}>
                                                        {triggerLabel(hint.trigger_event)}
                                                    </Box>
                                                </Typography>
                                            </Box>

                                            {/* Right slot: eye icon + toggle or read-only chip */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                                {/* Preview eye icon — ghost style matching WhatsAppTemplateListItem star */}
                                                <Tooltip title={tplObj ? 'Preview template' : 'Template not loaded'} arrow placement="left">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setPreviewTemplate(tplObj)}
                                                            disabled={!tplObj}
                                                            sx={{
                                                                width: 28, height: 28,
                                                                color: 'text.disabled',
                                                                '&:hover': {
                                                                    color: 'primary.main',
                                                                    bgcolor: alpha(theme.palette.success.main, 0.08),
                                                                },
                                                                '&.Mui-disabled': { opacity: 0.35 },
                                                            }}
                                                        >
                                                            <Icon icon="mdi:eye-outline" width={16} height={16} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                {/* Toggle (admin/superadmin) or read-only chip */}
                                                {canToggleAutomation ? (
                                                    <Tooltip
                                                        title={hint.is_enabled ? 'Turn automation OFF' : 'Turn automation ON'}
                                                        placement="left"
                                                        arrow
                                                    >
                                                        <Switch
                                                            size="small"
                                                            color="success"
                                                            checked={hint.is_enabled}
                                                            disabled={isInflight}
                                                            onChange={(e) => setWorkflowEnabled(hint.workflow_id, e.target.checked)}
                                                            sx={{ flexShrink: 0 }}
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label={hint.is_enabled ? 'ON' : 'OFF'}
                                                        sx={{
                                                            height: 18,
                                                            fontSize: '0.62rem',
                                                            fontWeight: 700,
                                                            flexShrink: 0,
                                                            bgcolor: hint.is_enabled ? alpha(theme.palette.success.main, 0.12) : 'rgba(145,158,171,0.12)',
                                                            color: hint.is_enabled ? theme.palette.success.darker : '#637381',
                                                            borderRadius: '8px',
                                                            '& .MuiChip-label': { px: 0.75 },
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    );
                                })
                            )}
                        </Box>
                    </Box>
                )}

            </Box>

            {/* Template browser/manager modal — same component the composer uses */}
            <WhatsappTemplateModal
                open={templateModalOpen}
                onClose={() => setTemplateModalOpen(false)}
                currentIndex={templateModalIndex}
                templates={templateList}
                onNext={() => setTemplateModalIndex((i) => Math.min(i + 1, templateList.length - 1))}
                onPrevious={() => setTemplateModalIndex((i) => Math.max(i - 1, 0))}
                onSelect={setTemplateModalIndex}
                onSend={() => {
                    // No thread context here — closing the modal is the safest
                    // outcome. Operators wanting to send pick a conversation
                    // first; create/edit work fine without one.
                    setTemplateModalOpen(false);
                }}
                initialStatus={templateModalStatus}
                providerAccount={providerAccount}
            />

            {/* Template preview drawer — opened via the eye icon in the Automations rows */}
            <TemplatePreviewDrawer
                open={!!previewTemplate}
                onClose={() => setPreviewTemplate(null)}
                template={previewTemplate}
                type="whatsapp"
            />
        </Box>
    );
};

export default SearchLeadForWhatsapp;
